/**
 * The full profile.
 *
 * Built against the reference (exec-3996fc05): a banner with the person over
 * it, their actions beside their name, then Overview / Games / Activity, with
 * shared friends in a rail - beside the profile from 1280px, below it under
 * that.
 *
 * Everything a player chose still drives the hero: banner, banner colour,
 * frame, profile frame, profile effect, banner effect and accent. Nothing that
 * was here is gone - level, badges, stats, linked accounts, the Riot card,
 * account details and the staff panel all moved, none were dropped.
 *
 * Reached from the popout card, and also rendered inside a panel; the props
 * below are for that case.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/lib/api';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import * as Icons from '@/icons';
import { CrewTag } from '@/components/Crew';
import { openCrewCard } from '@/components/CrewTag';
import { FramedAvatar, frameGlow } from '@/components/FramedAvatar';
import { FrameEffect } from '@/components/Frames';
import { Avatar } from '@/components/Avatar';
import { BadgeTray, RoleTag, TierTag, UserName, absolute } from '@/components/user';
import { Tip } from '@/components/HoverCard';
import { Icon } from '@/icons/Icon';
import { gameBanner, gameIconArt } from '@/lib/gameArt';
import { Button, Empty, IconButton, Tag, useToast } from '@/components/ui';
import { EmojiText } from '@/components/EmojiText';
import { RiotCard } from '@/components/Connections';
import { NowPlaying } from '@/components/NowPlaying';
import { AvailableTag } from '@/components/Availability';
import { InviteToParty, useUserMenu } from '@/components/userMenu';
import { useMinWidth } from '@/lib/viewport';

type Relation = { status: string; actorId: string; id: string } | null;
/* No activity feed: what somebody is doing is shown live, the way Discord
   does it - the game they have open right now - not as a log of past events. */
type Tab = 'overview' | 'games' | 'badges';
const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'games', label: 'Games' },
  { id: 'badges', label: 'Badges' }
];

const PRESENCE: Record<string, { dot: string; label: string }> = {
  online: { dot: 'bg-good', label: 'Online' },
  idle: { dot: 'bg-warn', label: 'Away' },
  dnd: { dot: 'bg-bad', label: 'Do not disturb' },
  offline: { dot: 'bg-ghost', label: 'Offline' },
  invisible: { dot: 'bg-ghost', label: 'Offline' }
};

export function ProfileView({ username, onNavigate, onOpenUser, compact }: {
  username: string;
  /**
   * The windowed shape: identity in a column on the left, everything else
   * behind tabs on the right. Used by the panel; the page keeps the long
   * single column, which is right for something you navigated to on purpose.
   */
  compact?: boolean;
  /** Called before navigating away, so a panel can close itself first. */
  onNavigate?: () => void;
  /**
   * Open somebody else from inside this profile - a mutual friend, usually.
   * The panel stacks a second profile on top rather than swapping this one
   * out, so going back lands you where you were.
   */
  onOpenUser?: (username: string) => void;
}) {
  const me = useSession((s) => s.me);
  const [tab, setTab] = useState<Tab>('overview');
  const railBeside = useMinWidth(1280);

  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['user', username],
    queryFn: () => endpoints.user(username)
  });

  if (isPending) {
    return (
      <div className="min-h-0 flex-1 overflow-hidden" aria-busy="true" aria-label="Loading profile">
        <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-5 px-4 py-5 sm:px-6 xl:px-8">
          <div className="h-[380px] animate-pulse rounded-[var(--radius-xl)] bg-surface-3" />
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="h-[220px] animate-pulse rounded-[var(--radius-xl)] bg-surface-3" />
            <div className="h-[220px] animate-pulse rounded-[var(--radius-xl)] bg-surface-3" />
          </div>
        </div>
      </div>
    );
  }
  if (isError || !data) {
    const message = (error as Error)?.message ?? '';
    const missing = /not found|no player|no such/i.test(message);
    return (
      <div className="mx-auto w-full max-w-[640px] px-4 py-10">
        <Empty
          role={missing ? 'status' : 'alert'}
          title={missing ? 'No player called @' + username : 'Could not load this profile'}
          note={missing ? 'They may have changed their username or deleted their account.' : 'Something went wrong. Everything else is still working.'}
          actions={
            <>
              {!missing ? <Button kind="outline" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Trying…' : 'Try again'}</Button> : null}
              <Button kind="primary" onClick={() => { onNavigate?.(); go('friends'); }}>Go to Friends</Button>
            </>
          }
        />
      </div>
    );
  }

  const user = data.user;
  const isMe = me?.id === user.id;
  const mutuals = data.mutuals ?? [];

  const rail = (
    <div className="flex flex-col gap-5">
      <PlayerLevel user={user} />
      {!isMe ? <MutualFriends mutuals={mutuals} onOpenUser={onOpenUser} onNavigate={onNavigate} /> : null}
      <LinkedAccounts user={user} />
      <AccountCard user={user} />
    </div>
  );

  /*
   * The windowed profile: identity on the left, everything else on the right.
   *
   * The page version is one column of nine cards, which is fine on a page
   * you scrolled to on purpose and far too much in a panel somebody opened
   * to check one thing. The identity - picture, name, bio, member since,
   * connections, level - is what people actually came for, so it stands
   * still in a column of its own, and the rest goes behind tabs beside it.
   *
   * Each column scrolls on its own inside a window that does not, so the
   * name never scrolls away from the badges that belong to it.
   */
  if (compact) {
    return (
      <div className="flex min-h-0 flex-1 overflow-hidden" data-profile-of={user.username}>
        <div className="flex min-h-0 w-[288px] flex-none flex-col overflow-y-auto border-r border-line-soft">
          <CompactIdentity user={user} isMe={isMe} relation={data.relation ?? null} onLeave={onNavigate} />
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div
            role="tablist"
            aria-label="Profile sections"
            className="flex flex-none items-center gap-1 border-b border-line-soft px-4 pt-3"
          >
            {TABS.map((t) => {
              const on = t.id === tab;
              return (
                <button
                  key={t.id}
                  id={'profile-tab-' + t.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  aria-controls="profile-panel"
                  tabIndex={on ? 0 : -1}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    'relative rounded-t-[8px] px-3 pb-2.5 text-[13.5px] font-semibold outline-none',
                    'transition-colors duration-[var(--t-hover)]',
                    on ? 'text-ink' : 'text-mute hover:text-dim',
                    'focus-visible:shadow-[0_0_0_2px_var(--accent)]'
                  )}
                >
                  {t.label}
                  {/* The underline moves rather than each tab carrying its
                      own border, so nothing shifts by a pixel on selection. */}
                  {on ? <span className="absolute inset-x-2 -bottom-px h-[2px] rounded-full bg-accent" /> : null}
                </button>
              );
            })}
          </div>

          <div
            id="profile-panel"
            role="tabpanel"
            aria-labelledby={'profile-tab-' + tab}
            className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
          >
            {tab === 'overview' ? (
              <>
                {user.playing ? <Card title="Playing"><NowPlaying user={user} /></Card> : null}
                <MainGame user={user} onNavigate={onNavigate} />
                <CrewCard user={user} onNavigate={onNavigate} />
                <FavouriteGames user={user} onSeeAll={() => setTab('games')} />
                <FeaturedBadges user={user} onSeeAll={() => setTab('badges')} />
                <Stats user={user} />
                {!isMe && mutuals.length ? (
                  <MutualFriends mutuals={mutuals} onOpenUser={onOpenUser} onNavigate={onNavigate} />
                ) : null}
              </>
            ) : tab === 'games' ? (
              <Card title="Games" note={user.games?.length ? String(user.games.length) : undefined}>
                <GameGrid user={user} />
              </Card>
            ) : (
              <Card title="Badges" note={user.badges.length ? String(user.badges.length) : undefined}>
                {user.badges.length
                  ? <BadgeTray user={user} max={99} size="lg" />
                  : <p className="m-0 py-6 text-center text-[14px] text-mute">No badges yet.</p>}
              </Card>
            )}
            {data.admin ? <StaffPanel admin={data.admin} user={user} /> : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-profile-of={user.username}>
      <div className="mx-auto flex w-full max-w-[1440px] items-start gap-6 px-4 py-5 sm:px-6 xl:px-8">
        <div className="flex min-w-0 flex-1 flex-col gap-5">
          <Hero user={user} isMe={isMe} relation={data.relation ?? null} onLeave={onNavigate} tab={tab} onTab={setTab} />

          <div id="profile-panel" role="tabpanel" aria-labelledby={'profile-tab-' + tab} className="flex flex-col gap-5">
            {tab === 'overview' ? (
              <>
                {user.playing ? (
                  <Card title="Playing">
                    <NowPlaying user={user} />
                  </Card>
                ) : null}

                {/* The one game they are actually here for, drawn large. A
                    profile that opens with four equal tiles makes you read
                    all four to learn the one thing you came for. */}
                <About user={user} />

                <MainGame user={user} onNavigate={onNavigate} />

                <CrewCard user={user} onNavigate={onNavigate} />

                <FavouriteGames user={user} onSeeAll={() => setTab('games')} />

                <FeaturedBadges user={user} onSeeAll={() => setTab('badges')} />

                <Stats user={user} />
                {user.connections?.riot ? <RiotCard link={user.connections.riot} /> : null}
              </>
            ) : tab === 'games' ? (
              <Card title="Games" note={user.games?.length ? String(user.games.length) : undefined}>
                <GameGrid user={user} />
              </Card>
            ) : (
              <Card title="Badges" note={user.badges.length ? String(user.badges.length) : undefined}>
                {user.badges.length ? (
                  <BadgeTray user={user} max={99} size="lg" />
                ) : (
                  <p className="m-0 py-6 text-center text-[14px] text-mute">No badges yet.</p>
                )}
              </Card>
            )}

            {!railBeside ? rail : null}
            {data.admin ? <StaffPanel admin={data.admin} user={user} /> : null}
          </div>
        </div>

        {railBeside ? <aside aria-label="About this player" className="w-[340px] flex-none">{rail}</aside> : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- hero */

function Hero({ user, isMe, relation, onLeave, tab, onTab }: {
  user: PublicUser;
  isMe: boolean;
  relation: Relation;
  onLeave?: () => void;
  tab: Tab;
  onTab: (t: Tab) => void;
}) {
  const banner = user.banner ? absolute(user.banner) : null;
  /* The phone crop, when there is one. Set by the picture framer in
     Settings; without it the wide one is used on every width, which is what
     everybody had before. */
  const bannerTall = user.bannerTall ? absolute(user.bannerTall) : null;
  const glow = frameGlow(user);
  const roomy = useMinWidth(640);
  const presence = PRESENCE[user.status] ?? PRESENCE.offline!;
  const menu = useUserMenu(user, relation, { onNavigate: onLeave });
  const [inviting, setInviting] = useState(false);
  const mine = useQuery({
    queryKey: ['parties', 'mine'],
    queryFn: () => endpoints.parties({ scope: 'mine' }),
    enabled: !isMe
  });
  const canInvite = (mine.data?.parties ?? []).some((p) => !p.closed);

  function onTabKey(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = TABS.findIndex((t) => t.id === tab);
    const next = TABS[(i + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length]!.id;
    onTab(next);
    document.getElementById('profile-tab-' + next)?.focus();
  }

  return (
    <section
      aria-label={user.displayName + "'s profile"}
      style={{
        ['--profile-accent' as string]: user.accent ?? 'var(--accent)',
        ...(glow ? { ['--frame-glow' as string]: glow } : {})
      }}
      className={cn(
        'relative overflow-hidden rounded-[var(--radius-xl)] border bg-surface-1',
        glow
          ? 'border-[color-mix(in_srgb,var(--frame-glow)_34%,var(--line))]'
          : 'border-line-soft'
      )}
    >
      {/*
        The banner.

        Shorter than it was - at 220px an empty gradient was the largest
        thing on the page and the name started below the fold on a laptop.
        It carries a scrim at the foot now rather than being kept clear of
        text, which is what lets the picture run behind the name instead of
        stopping short of it, and it is the one place on this page with any
        colour in it.
      */}
      <div
        className="banner-in pointer-events-none relative h-[132px] overflow-hidden sm:h-[176px]"
        style={{
          background: banner
            ? 'var(--bg-3)'
            : user.bannerColor
              ? `linear-gradient(115deg, ${user.bannerColor}, color-mix(in srgb, ${user.bannerColor} 45%, var(--bg-3)))`
              : 'linear-gradient(115deg, color-mix(in srgb, var(--profile-accent) 42%, var(--bg-3)), color-mix(in srgb, var(--profile-accent) 12%, var(--bg-3)) 70%)'
        }}
      >
        {banner ? (
          bannerTall ? (
            /* Two sources, one element: the browser picks by width, so there
               is no layout shift and only one of them is ever fetched. */
            <picture>
              <source media="(max-width: 700px)" srcSet={bannerTall} />
              <img src={banner} alt="" className="h-full w-full object-cover" />
            </picture>
          ) : (
            <img src={banner} alt="" className="h-full w-full object-cover" />
          )
        ) : null}
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-[62%]"
          style={{ background: 'linear-gradient(180deg, transparent, color-mix(in srgb, var(--bg-1) 88%, transparent))' }}
        />
        <FrameEffect frame={user.frame} />
      </div>

      <div className="relative px-4 pb-1 sm:px-6">
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          {/* The picture in the shape you chose for avatars, lifted off the
              banner by a border in the card's own colour - no coloured ring
              and no frame circled round it. */}
          <span className={cn('avatar-pop flex-none leading-[0]', roomy ? '-mt-[76px]' : '-mt-[54px]')}>
            <FramedAvatar user={user} size={roomy ? 132 : 96} presence animate frameless ring="var(--bg-1)" />
          </span>

          {/* A floor on the name's width, so the buttons wrap under it rather
              than squeezing it to one letter - which is what happened at 1440
              with a rail beside the profile. */}
          <div className="min-w-[min(100%,280px)] flex-1 pt-3">
            <h1 className="m-0 flex min-w-0 flex-wrap items-center gap-2.5 text-[26px] leading-[1.08] font-extrabold tracking-[-.03em] text-ink sm:text-[34px]">
              <UserName user={user} profile={false} />
              <CrewTag crew={user.crew} className="translate-y-[2px]" />
              {user.role === 'admin' || user.role === 'mod' ? (
                <Tip text={user.role === 'admin' ? 'Administrator' : 'Moderator'}>
                  <span className="inline-flex text-accent" role="img" aria-label={user.role === 'admin' ? 'Administrator' : 'Moderator'}>
                    <Icons.Verified size={22} />
                  </span>
                </Tip>
              ) : null}
            </h1>
            <p className="mt-1 mb-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14.5px] text-mute">
              <span>@{user.username}</span>
              <span className="flex items-center gap-1.5">
                <span aria-hidden className={cn('h-2 w-2 rounded-full', presence.dot)} />
                {presence.label}
              </span>
            </p>
            {user.customStatus ? (
              <p className="mt-1.5 mb-0 text-[15px] text-dim"><EmojiText text={user.customStatus} size={17} /></p>
            ) : null}
            <div className="mt-2.5 flex flex-wrap items-center gap-2 empty:hidden">
              <AvailableTag available={user.available} />
              <TierTag tier={user.tier} />
              <RoleTag role={user.role} />
              {user.banned ? <Tag kind="bad">SUSPENDED</Tag> : null}
            </div>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 pb-1 sm:w-auto">
            {isMe ? (
              <Button kind="primary" icon={<Icons.Edit size={15} />} onClick={() => { onLeave?.(); go('settings/profile'); }}>
                Edit profile
              </Button>
            ) : (
              <>
                <Button kind="outline" icon={<Icon name="chat" size={16} />} onClick={() => { onLeave?.(); go('chat/' + user.username); }}>
                  Message
                </Button>
                {canInvite ? (
                  <Button kind="primary" icon={<Icon name="squads" size={16} />} onClick={() => setInviting(true)}>
                    Invite to party
                  </Button>
                ) : null}
                <FriendAction user={user} relation={relation} />
                <IconButton
                  label={'More options for ' + user.displayName}
                  className="h-[var(--h-md)] w-[var(--h-md)] border border-line-soft"
                  onClick={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    menu.openAt(Math.max(8, r.right - 214), r.bottom + 4);
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
                  </svg>
                </IconButton>
              </>
            )}
          </div>
        </div>

        {/* Level moved into the rail, where it sits beside About - the hero
            is who they are, not a progress bar. */}
        <div
          role="tablist"
          aria-label="Profile sections"
          onKeyDown={onTabKey}
          className="mt-4 -mb-px flex gap-1 border-t border-line-soft pt-1"
        >
          {TABS.map((t) => {
            const on = t.id === tab;
            return (
              <button
                key={t.id}
                id={'profile-tab-' + t.id}
                type="button"
                role="tab"
                aria-selected={on}
                aria-controls="profile-panel"
                tabIndex={on ? 0 : -1}
                onClick={() => onTab(t.id)}
                className={cn(
                  'relative h-[46px] px-3.5 text-[15px] font-semibold outline-none sm:px-4',
                  'transition-colors duration-[var(--t-hover)] ease-[var(--ease)] focus-visible:shadow-[inset_0_0_0_2px_var(--accent)]',
                  on ? 'text-accent' : 'text-dim hover:text-ink'
                )}
              >
                {t.label}
                <span
                  aria-hidden
                  className={cn(
                    'absolute inset-x-3 bottom-0 h-[3px] rounded-full bg-accent transition-opacity duration-[var(--t-hover)]',
                    on ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </button>
            );
          })}
        </div>
      </div>

      {menu.menu}
      {inviting ? <InviteToParty user={user} onClose={() => setInviting(false)} /> : null}
    </section>
  );
}

/* ------------------------------------------------------------ pieces */

function Card({ title, note, action, children, className }: {
  title: string;
  note?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const id = 'profile-' + title.toLowerCase().replace(/\W+/g, '-');
  return (
    <section aria-labelledby={id} className={cn('rounded-[var(--radius-xl)] border border-line-soft bg-surface-1 p-4 sm:p-5', className)}>
      <div className="mb-3 flex items-center gap-2">
        <h2 id={id} className="m-0 text-[18px] font-bold tracking-[-.01em] text-ink">{title}</h2>
        {note ? <span className="text-[14px] font-semibold text-mute tabular-nums">{note}</span> : null}
        <span className="flex-1" />
        {action}
      </div>
      {children}
    </section>
  );
}

function SeeAll({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[6px] px-1 text-[14px] font-semibold text-accent outline-none hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]"
    >
      See all
    </button>
  );
}

/** How far through their level they are, as the reference draws it. */
function PlayerLevel({ user, bare }: { user: PublicUser; bare?: boolean }) {
  const pct = user.levelNeed ? Math.round((user.levelInto / user.levelNeed) * 100) : 0;
  /* In the windowed profile this sits in a column that is mostly border
     already, so it goes in without one. */
  const Shell = bare
    ? ({ children }: { children: React.ReactNode }) => <>{children}</>
    : ({ children }: { children: React.ReactNode }) => <Card title="Player level">{children}</Card>;
  return (
    <Shell>
      <div className="flex items-center gap-4">
        <span
          className="grid h-[54px] w-[54px] flex-none place-items-center text-[22px] font-extrabold text-white"
          style={{
            background: 'linear-gradient(145deg, var(--profile-accent), color-mix(in srgb, var(--profile-accent) 55%, #000))',
            clipPath: 'polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)'
          }}
        >
          {user.level}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[17px] font-bold text-ink">Level {user.level}</span>
            <span className="text-[14px] text-mute tabular-nums">
              {user.levelInto.toLocaleString()} / {user.levelNeed.toLocaleString()} XP
            </span>
          </div>
          <div
            className="mt-2 h-2 overflow-hidden rounded-full bg-surface-3"
            role="progressbar"
            aria-label={'Level ' + user.level + ' progress'}
            aria-valuemin={0}
            aria-valuemax={user.levelNeed}
            aria-valuenow={user.levelInto}
          >
            <i className="block h-full rounded-full transition-[width] duration-700 ease-[var(--ease-out)]" style={{ width: pct + '%', background: 'linear-gradient(90deg, var(--profile-accent), color-mix(in srgb, var(--profile-accent) 45%, #fff))' }} />
          </div>
          <p className="mt-1.5 mb-0 text-[13px] text-mute">Progress to level {user.level + 1}</p>
        </div>
      </div>
    </Shell>
  );
}

/**
 * The left-hand column of the windowed profile.
 *
 * Everything that answers "who is this" and nothing that does not: the
 * picture, the name, what they are called, their badges, the two or three
 * things you might do about them, and then the short facts - bio, when they
 * joined, what they have linked, their level.
 *
 * Deliberately not cards. A stack of bordered boxes in a 288px column is
 * mostly border; these are labelled paragraphs with a rule between them,
 * which is what makes the column read as one person rather than six panels.
 */
function CompactIdentity({ user, isMe, relation, onLeave }: {
  user: PublicUser;
  isMe: boolean;
  relation: Relation;
  onLeave?: () => void;
}) {
  const banner = user.banner ? absolute(user.banner) : null;
  const presence = PRESENCE[user.status] ?? PRESENCE.offline!;
  const menu = useUserMenu(user, relation, { onNavigate: onLeave });
  const [inviting, setInviting] = useState(false);
  const mine = useQuery({
    queryKey: ['parties', 'mine'],
    queryFn: () => endpoints.parties({ scope: 'mine' }),
    enabled: !isMe
  });
  const canInvite = (mine.data?.parties ?? []).some((p) => !p.closed);

  return (
    <div
      style={{ ['--profile-accent' as string]: user.accent ?? 'var(--accent)' }}
      className="flex flex-col"
    >
      {/* A short banner: enough to carry the colour, not enough to be the
          first thing you read. */}
      <div
        className="relative h-[84px] flex-none"
        style={{
          background: banner
            ? 'var(--bg-3)'
            : user.bannerColor
              ? `linear-gradient(115deg, ${user.bannerColor}, color-mix(in srgb, ${user.bannerColor} 45%, var(--bg-3)))`
              : 'linear-gradient(115deg, color-mix(in srgb, var(--profile-accent) 42%, var(--bg-3)), color-mix(in srgb, var(--profile-accent) 12%, var(--bg-3)) 70%)'
        }}
      >
        {banner ? <img src={banner} alt="" className="h-full w-full object-cover" /> : null}
      </div>

      <div className="flex flex-col gap-3 px-4 pb-4">
        <span className="-mt-[34px] flex-none leading-[0]">
          <FramedAvatar user={user} size={72} presence animate ring="var(--bg-0)" />
        </span>

        <div className="min-w-0">
          <h1 className="m-0 flex min-w-0 items-center gap-1.5 text-[19px] leading-[1.1] font-extrabold tracking-[-.02em] text-ink">
            <UserName user={user} profile={false} className="min-w-0 truncate" />
            {user.role === 'admin' || user.role === 'mod' ? (
              <Tip text={user.role === 'admin' ? 'Administrator' : 'Moderator'}>
                <span className="inline-flex flex-none text-accent" role="img" aria-label={user.role === 'admin' ? 'Administrator' : 'Moderator'}>
                  <Icons.Verified size={15} />
                </span>
              </Tip>
            ) : null}
          </h1>
          <p className="m-0 mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-mute">
            <span className="truncate">@{user.username}</span>
            <CrewTag crew={user.crew} />
          </p>
          <p className="m-0 mt-1 flex items-center gap-1.5 text-[12.5px] text-mute">
            <span aria-hidden className={cn('h-2 w-2 flex-none rounded-full', presence.dot)} />
            {presence.label}
          </p>
        </div>

        {user.customStatus ? (
          <p className="m-0 text-[13px] text-dim"><EmojiText text={user.customStatus} size={15} /></p>
        ) : null}

        {user.badges.length ? <BadgeTray user={user} max={8} size="sm" /> : null}

        {/* Two things and a menu. A column this wide cannot hold five
            buttons without wrapping them into a grid of stubs. */}
        <div className="flex items-center gap-1.5">
          {isMe ? (
            <Button kind="primary" className="flex-1" icon={<Icons.Edit size={14} />} onClick={() => { onLeave?.(); go('settings/profile'); }}>
              Edit profile
            </Button>
          ) : (
            <>
              <Button kind="primary" className="flex-1" icon={<Icon name="chat" size={15} />} onClick={() => { onLeave?.(); go('chat/' + user.username); }}>
                Message
              </Button>
              {canInvite ? (
                <IconButton label="Invite to party" onClick={() => setInviting(true)} className="h-[var(--h-md)] w-[var(--h-md)] flex-none rounded-[var(--radius-s)] border border-line">
                  <Icon name="squads" size={15} />
                </IconButton>
              ) : null}
            </>
          )}
          <IconButton
            label={'More options for ' + user.displayName}
            onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); menu.openAt(Math.max(8, r.right - 214), r.bottom + 4); }}
            className="h-[var(--h-md)] w-[var(--h-md)] flex-none rounded-[var(--radius-s)] border border-line"
          >
            <Icon name="more" size={15} />
          </IconButton>
        </div>

        <Rule />

        {user.bio ? (
          <Fact label="Bio"><p className="m-0 text-[13px] leading-[1.5] whitespace-pre-wrap text-dim">{user.bio}</p></Fact>
        ) : null}

        <Fact label="Member since">
          <p className="m-0 text-[13px] text-dim">
            {new Date(user.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </Fact>

        <Fact label="Level">
          <PlayerLevel user={user} bare />
        </Fact>

        <LinkedAccounts user={user} bare />
      </div>

      {menu.menu}
      {inviting ? <InviteToParty user={user} onClose={() => setInviting(false)} /> : null}
    </div>
  );
}

/** A labelled paragraph. The column's unit, instead of a card. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="m-0 mb-1 text-[11px] font-bold tracking-[.06em] text-mute uppercase">{label}</p>
      {children}
    </div>
  );
}

function Rule() {
  return <span aria-hidden className="block h-px w-full bg-line-soft" />;
}

/** The badges they wear, with the way to the rest. */
function FeaturedBadges({ user, onSeeAll }: { user: PublicUser; onSeeAll: () => void }) {
  if (!user.badges.length) return null;
  return (
    <Card title="Featured badges" action={user.badges.length > 5 ? <SeeAll onClick={onSeeAll} /> : null}>
      <BadgeTray user={user} max={5} size="lg" />
    </Card>
  );
}

function About({ user }: { user: PublicUser }) {
  const catalog = useSession((s) => s.catalog);
  const regionName = catalog?.regions.find((r) => r.id === user.region)?.name ?? user.region;
  const playstyleName = catalog?.playstyles.find((p) => p.id === user.playstyle)?.name ?? user.playstyle;

  /* Labelled rows rather than a list of bare facts: "Oceania" on its own
     tells you nothing, and a profile is read by somebody deciding whether to
     play with a stranger. */
  const facts: [string, string, string][] = [
    ['translate', 'Languages', user.languages.length ? user.languages.join(', ') : 'Not listed'],
    ['globe', 'Region', regionName || 'Anywhere'],
    ['target', 'Playstyle', playstyleName || 'Not set'],
    ['mic', 'Voice', user.mic ? 'Has a mic' : 'No mic']
  ];

  return (
    <Card title="About">
      {/* The bio first, because it is the only part they wrote themselves. */}
      <div className="flex items-start gap-3 border-b border-line-soft pb-4">
        <Icon name="profile" size={18} className="mt-0.5 flex-none text-ghost" />
        <p className={cn('m-0 min-w-0 flex-1 text-[14.5px] leading-relaxed whitespace-pre-wrap', user.bio ? 'selectable text-dim' : 'text-mute')}>
          {user.bio ? <EmojiText text={user.bio} size={17} /> : 'No bio yet.'}
        </p>
      </div>

      {/* Four facts in a grid rather than a column of right-aligned rows:
          they are short, they pair naturally, and a stack of four wastes the
          width the card already has. */}
      <dl className="m-0 mt-4 grid gap-x-6 gap-y-4 p-0 sm:grid-cols-2">
        {facts.map(([icon, label, text]) => (
          <div key={label} className="flex items-start gap-3">
            <Icon name={icon} size={18} className="mt-0.5 flex-none text-ghost" />
            <div className="min-w-0">
              <dt className="text-[13px] text-mute">{label}</dt>
              <dd className={cn('m-0 mt-0.5 truncate text-[14.5px] font-semibold', label === 'Voice' && user.mic ? 'text-good' : 'text-ink')}>{text}</dd>
            </div>
          </div>
        ))}
      </dl>

      <p className="m-0 mt-4 flex items-center gap-2.5 border-t border-line-soft pt-3 text-[13px] text-mute">
        <Icon name="calendar" size={16} className="flex-none text-ghost" />
        Member since {new Date(user.createdAt).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })}
      </p>
    </Card>
  );
}

/* ------------------------------------------------- main game and crew */

/**
 * The game they are here for.
 *
 * Their first saved game, or whatever they have open right now - the same
 * choice the small card makes, so the two never disagree about somebody's
 * "main". The art is the game's own banner, and the button under it is the
 * only thing you would actually want to do with this information.
 */
function MainGame({ user, onNavigate }: { user: PublicUser; onNavigate?: () => void }) {
  const catalog = useSession((s) => s.catalog);
  const entry = (user.playing ? (user.games?.find((g) => g.id === user.playing) ?? { id: user.playing }) : null) ?? user.games?.[0] ?? null;
  if (!entry) return null;
  const meta = catalog?.games.find((g) => g.id === entry.id);
  const art = gameBanner(entry.id);
  const playstyle = catalog?.playstyles.find((p) => p.id === user.playstyle)?.name;
  const icon = gameIconArt(entry.id);

  return (
    <Card title={user.playing === entry.id ? 'Playing now' : 'Main game'}>
      <div className="flex flex-col gap-4 sm:flex-row">
        <span
          className="block h-[170px] w-full flex-none overflow-hidden rounded-[var(--radius-l)] bg-surface-3 sm:h-[190px] sm:w-[220px]"
          style={art ? undefined : { background: 'linear-gradient(135deg, ' + (meta?.color ?? 'var(--accent)') + ', var(--bg-3))' }}
        >
          {art ? <img src={art} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
        </span>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-3">
            <span
              className="grid h-11 w-11 flex-none place-items-center overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] border"
              style={{
                borderColor: 'color-mix(in srgb, ' + (meta?.color ?? 'var(--accent)') + ' 40%, transparent)',
                background: 'color-mix(in srgb, var(--bg-0) 65%, transparent)',
                color: meta?.color ?? 'var(--accent)'
              }}
            >
              {icon ? <img src={icon} alt="" className="h-full w-full object-cover" /> : <Icon name={meta?.icon ?? 'dice'} size={24} />}
            </span>
            <div className="min-w-0">
              <p className="m-0 truncate text-[19px] leading-tight font-bold text-ink">{meta?.name ?? entry.id}</p>
              <p className="m-0 truncate text-[14px] text-mute">{entry.rank || 'Unranked'}</p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 empty:hidden">
            {playstyle ? (
              <span className="flex items-center gap-1.5 rounded-full bg-surface-2 px-2.5 py-1 text-[12.5px] font-semibold text-dim">
                <Icon name="gamepad" size={13} className="text-mute" />
                {playstyle}
              </span>
            ) : null}
            {entry.roles?.length ? (
              <span className="rounded-full bg-surface-2 px-2.5 py-1 text-[12.5px] font-semibold text-dim">
                {entry.roles.slice(0, 3).join(' · ')}
              </span>
            ) : null}
          </div>

          {meta?.genre ? (
            <p className="mt-3 mb-0 text-[13.5px] leading-relaxed text-mute">
              {meta.genre}{meta.squad ? '. Usually ' + meta.squad + ' a side.' : '.'}
            </p>
          ) : null}

          <span className="flex-1" />
          <div className="mt-4">
            <Button
              kind="primary"
              icon={<Icon name="squads" size={16} />}
              onClick={() => { onNavigate?.(); go('parties?game=' + encodeURIComponent(entry.id)); }}
            >
              Find a party
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

/** The crew they wear, and the way into it. */
function CrewCard({ user, onNavigate }: { user: PublicUser; onNavigate?: () => void }) {
  const crew = user.crew;
  if (!crew) return null;
  return (
    <Card title="Crew">
      <button
        type="button"
        onClick={() => { onNavigate?.(); openCrewCard(crew.tag); }}
        className="flex w-full items-center gap-3.5 rounded-[var(--radius-l)] bg-surface-2 p-3.5 text-left outline-none transition-colors hover:bg-surface-3 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
      >
        <CrewTag crew={crew} size="lg" still />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15.5px] font-bold text-ink">{crew.name}</span>
          <span className="block truncate text-[13px] text-mute">
            {(crew.memberCount ?? 0) + (crew.memberCount === 1 ? ' member' : ' members')}
            {crew.level ? ' · Crew level ' + crew.level : ''}
          </span>
        </span>
        <span className="flex flex-none items-center gap-1 text-[13.5px] font-semibold text-accent">
          View crew
          <Icon name="chevronR" size={15} />
        </span>
      </button>
    </Card>
  );
}

/** Who you both know, in the column rather than the rail. */
function MutualFriends({ mutuals, onOpenUser, onNavigate }: {
  mutuals: PublicUser[];
  onOpenUser?: (username: string) => void;
  onNavigate?: () => void;
}) {
  if (!mutuals.length) return null;
  return (
    <Card title="Mutual friends" note={String(mutuals.length)}>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {mutuals.slice(0, 6).map((u) => {
          const p = PRESENCE[u.status] ?? PRESENCE.offline!;
          return (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => (onOpenUser ? onOpenUser(u.username) : (onNavigate?.(), go('u/' + u.username)))}
                className="flex w-full items-center gap-3 rounded-[var(--radius-l)] px-2 py-2 text-left outline-none transition-colors hover:bg-surface-2 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
              >
                <Avatar user={u} size={40} presence />
                <span className="min-w-0 flex-1">
                  <UserName user={u} profile={false} className="block truncate text-[14.5px] font-bold" />
                  <span className="flex items-center gap-1.5 text-[12.5px] text-mute">
                    <span aria-hidden className={cn('h-2 w-2 rounded-full', p.dot)} />
                    {p.label}
                  </span>
                </span>
                <Icon name="chevronR" size={15} className="flex-none text-ghost" />
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function FavouriteGames({ user, onSeeAll }: { user: PublicUser; onSeeAll: () => void }) {
  const games = user.games ?? [];
  return (
    <Card title="Favorite games" action={games.length > 4 ? <SeeAll onClick={onSeeAll} /> : null}>
      {games.length ? (
        <GameTiles ids={games.slice(0, 4).map((g) => g.id)} />
      ) : (
        <p className="m-0 py-6 text-center text-[14px] text-mute">No games on this profile yet.</p>
      )}
    </Card>
  );
}

function GameTiles({ ids }: { ids: string[] }) {
  const catalog = useSession((s) => s.catalog);
  return (
    <ul className="m-0 grid list-none grid-cols-2 gap-3 p-0 sm:grid-cols-4">
      {ids.map((id) => {
        const g = catalog?.games.find((x) => x.id === id);
        const art = gameBanner(id);
        return (
          <li key={id} className="min-w-0">
            <button
              type="button"
              onClick={() => go('parties?game=' + encodeURIComponent(id))}
              aria-label={'Browse ' + (g?.name ?? id) + ' parties'}
              className="group flex w-full flex-col gap-1.5 rounded-[var(--radius-l)] text-left outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              <span className="block aspect-[4/5] w-full overflow-hidden rounded-[var(--radius-l)] bg-surface-3">
                {art ? (
                  <img src={art} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[400ms] ease-[var(--ease-out)] motion-safe:group-hover:scale-[1.04]" />
                ) : (
                  <span className="grid h-full w-full place-items-center text-[20px] font-extrabold text-white/90" style={{ background: 'linear-gradient(135deg, ' + (g?.color ?? 'var(--accent)') + ', var(--art-base))' }}>
                    {g?.short ?? id.slice(0, 3).toUpperCase()}
                  </span>
                )}
              </span>
              <span className="truncate text-[14px] font-semibold text-ink">{g?.name ?? id}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function GameGrid({ user }: { user: PublicUser }) {
  const catalog = useSession((s) => s.catalog);
  const games = user.games ?? [];
  if (!games.length) return <p className="m-0 py-6 text-center text-[14px] text-mute">No games on this profile yet.</p>;
  return (
    <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 xl:grid-cols-3">
      {games.map((entry) => {
        const g = catalog?.games.find((x) => x.id === entry.id);
        const art = gameBanner(entry.id);
        return (
          <li key={entry.id} className="flex items-center gap-3 rounded-[var(--radius-l)] border border-line-soft bg-surface-2 p-2.5">
            <span className="h-[56px] w-[88px] flex-none overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] bg-surface-3">
              {art ? <img src={art} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-bold text-ink">{g?.name ?? entry.id}</span>
              <span className="block truncate text-[13px] text-mute">
                {[entry.rank, entry.roles?.join(', '), entry.hours ? entry.hours + 'h played' : null].filter(Boolean).join(' · ') || g?.genre || 'Game'}
              </span>
              {entry.ign ? <span className="block truncate text-[12.5px] text-mute">In game: {entry.ign}</span> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}


function Stats({ user }: { user: PublicUser }) {
  const items: [string, string, string][] = [
    ['Level', String(user.level ?? 1), (user.xp ?? 0).toLocaleString() + ' XP'],
    ['Voice', Math.round(user.stats.voiceSeconds / 3600) + 'h', 'in calls'],
    ['Messages', user.stats.messages.toLocaleString(), 'sent'],
    ['Badges', String(user.badges.length), 'earned']
  ];
  return (
    <Card title="Stats">
      <dl className="m-0 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {items.map(([k, v, n]) => (
          <div key={k} className="rounded-[var(--radius-l)] bg-surface-2 px-3 py-3">
            <dt className="text-[13px] font-semibold text-mute">{k}</dt>
            <dd className="m-0 mt-0.5 text-[22px] leading-tight font-extrabold text-ink tabular-nums">{v}</dd>
            <dd className="m-0 text-[12.5px] text-mute">{n}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function LinkedAccounts({ user, bare }: { user: PublicUser; bare?: boolean }) {
  const rows = Object.entries(user.connections ?? {})
    .map(([provider, link]) => ({ provider, link }))
    .filter(({ link }) => !!link);
  if (!rows.length) return null;

  if (bare) {
    return (
      <div className="min-w-0">
        <p className="m-0 mb-1 text-[11px] font-bold tracking-[.06em] text-mute uppercase">Connections</p>
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {rows.map(({ provider, link }) => <LinkedRow key={provider} provider={provider} link={link} />)}
        </ul>
      </div>
    );
  }

  return (
    <Card title="Linked accounts" note={String(rows.length)}>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {rows.map(({ provider, link }) => <LinkedRow key={provider} provider={provider} link={link} />)}
      </ul>
    </Card>
  );
}

const PROVIDER: Record<string, { name: string; colour: string; mark: React.ReactNode }> = {
  discord: {
    name: 'Discord',
    colour: '#5865f2',
    mark: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.3 5.3A16.6 16.6 0 0 0 15.2 4l-.5 1a15.4 15.4 0 0 0-5.4 0l-.5-1a16.5 16.5 0 0 0-4.1 1.3C2.1 9.2 1.4 13 1.7 16.8a16.7 16.7 0 0 0 5 2.5l1.1-1.7c-.6-.2-1.2-.5-1.7-.9l.4-.3a11.9 11.9 0 0 0 10.2 0l.4.3c-.5.4-1.1.7-1.7.9l1.1 1.7a16.6 16.6 0 0 0 5-2.5c.4-4.4-.7-8.2-2.2-11.5ZM8.7 14.5c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2Zm6.6 0c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2Z" />
      </svg>
    )
  },
  riot: {
    name: 'Riot Games',
    colour: '#d13639',
    mark: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M13.5 2 2 7.3l2.9 11 2.2-.3-.6-7.2.7-.3 1.3 7.3 3.7-.5-.6-8 .7-.3 1.4 8.1 3.7-.5-.6-8.9.8-.3 1.5 9 2.7-.4L22 4.3ZM13 19.6l.2 2.4 7.2 1-.4-4.5Z" />
      </svg>
    )
  }
};

/** One linked account: their picture and name there, where it is, and whether it is proven. */
function LinkedRow({ provider, link }: { provider: string; link: NonNullable<PublicUser['connections']>[string] }) {
  const meta = PROVIDER[provider] ?? { name: provider, colour: 'var(--accent)', mark: <Icon name="link" size={16} /> };
  const [broken, setBroken] = useState(false);
  const picture = !broken && typeof link.avatar === 'string' && /^https:\/\/cdn\.discordapp\.com\//.test(link.avatar) ? link.avatar : null;
  const name = link.username || link.riotId || meta.name;
  const since = link.linkedAt ? new Date(link.linkedAt).toLocaleDateString([], { month: 'short', year: 'numeric' }) : null;

  return (
    <li className="group flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] border border-line-soft bg-surface-2 p-2.5 pr-3 transition-colors duration-[250ms] hover:border-line">
      <span className="relative flex-none">
        {picture ? (
          <img
            src={picture}
            alt=""
            width={44}
            height={44}
            referrerPolicy="no-referrer"
            onError={() => setBroken(true)}
            className="h-11 w-11 rounded-full object-cover"
          />
        ) : (
          <span className="grid h-11 w-11 place-items-center rounded-full text-white" style={{ background: meta.colour }}>{meta.mark}</span>
        )}
        {/* The service's own mark on the corner, so a picture still says where it is from. */}
        {picture ? (
          <span className="absolute -right-1 -bottom-1 grid h-5 w-5 place-items-center rounded-full text-white ring-2 ring-[var(--bg-2)] [&_svg]:h-3 [&_svg]:w-3" style={{ background: meta.colour }} aria-hidden>
            {meta.mark}
          </span>
        ) : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-bold text-ink">{name}</span>
        <span className="block truncate text-[12.5px] text-mute">
          {meta.name}{link.region ? ' · ' + String(link.region).toUpperCase() : ''}{since ? ' · since ' + since : ''}
        </span>
      </span>
      {link.verified ? (
        <span className="flex flex-none items-center gap-1 rounded-full bg-[color-mix(in_srgb,var(--good)_14%,transparent)] px-2 py-0.5 text-[12px] font-semibold text-good">
          <Icon name="check" size={12} />
          Verified
        </span>
      ) : (
        <span className="flex-none rounded-full bg-surface-3 px-2 py-0.5 text-[12px] font-semibold text-mute">Unverified</span>
      )}
    </li>
  );
}

function AccountCard({ user }: { user: PublicUser }) {
  const catalog = useSession((s) => s.catalog);
  const playstyleName = catalog?.playstyles.find((p) => p.id === user.playstyle)?.name ?? user.playstyle;
  const joined = new Date(user.createdAt);
  const days = Math.max(0, Math.floor((Date.now() - user.createdAt) / 86400000));
  const age = days < 1 ? 'Joined today' : days < 60 ? days + ' days on Pugo' : days < 730 ? Math.floor(days / 30) + ' months on Pugo' : Math.floor(days / 365) + ' years on Pugo';
  const rows: { icon: string; label: string; value: string; note?: string }[] = [
    { icon: 'calendar', label: 'Member since', value: joined.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' }), note: age },
    { icon: 'dice', label: 'Playstyle', value: playstyleName || 'Not set' },
    { icon: 'star', label: 'Level', value: 'Level ' + user.level, note: (user.xp ?? 0).toLocaleString() + ' XP' }
  ];
  if (user.tier !== 'none') rows.push({ icon: 'crown', label: 'Membership', value: user.tier.charAt(0).toUpperCase() + user.tier.slice(1) });
  if (user.crew) rows.push({ icon: 'friends', label: 'Crew', value: user.crew.name ?? user.crew.tag, note: user.crew.tag });

  return (
    <Card title="Account details">
      <dl className="m-0 flex flex-col gap-1">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] px-1.5 py-2 transition-colors duration-[250ms] hover:bg-surface-2">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-accent-soft text-accent">
              <Icon name={r.label === 'Crew' ? 'friends' : r.icon} size={16} />
            </span>
            <span className="min-w-0 flex-1">
              <dt className="text-[12.5px] font-medium text-mute">{r.label}</dt>
              <dd className="m-0 truncate text-[14.5px] font-semibold text-ink">
                {r.value}
                {r.note ? <span className="ml-1.5 text-[12.5px] font-normal text-mute">{r.note}</span> : null}
              </dd>
            </span>
          </div>
        ))}
      </dl>
      <p className="mt-3 mb-0 flex items-center gap-2 border-t border-line-soft pt-3 text-[12.5px] text-mute">
        <span className="font-mono">@{user.username}</span>
        {user.role !== 'user' ? <span className="rounded-full bg-accent-soft px-2 py-px text-[11.5px] font-semibold text-accent">{user.role === 'admin' ? 'Admin' : 'Moderator'}</span> : null}
      </p>
    </Card>
  );
}

/* ------------------------------------------------------- staff panel */

function StaffPanel({ admin, user }: { admin: Record<string, unknown>; user: PublicUser }) {
  const [open, setOpen] = useState(false);
  const a = admin as Record<string, any>;

  const sections: [string, [string, string][]][] = [
    ['Identity', [
      ['Account id', String(a.id)],
      ['Username', '@' + user.username],
      ['Role', String(a.role)],
      ['Membership', a.tier === 'none' ? 'Standard' : String(a.tier).toUpperCase()],
      ['Suspended', a.banned ? (a.banned.reason ?? 'no reason given') : 'No']
    ]],
    ['Activity', [
      ['Created', new Date(a.createdAt).toLocaleString()],
      ['Last seen', new Date(a.lastSeen).toLocaleString()],
      ['Live sessions', String(a.sessions)],
      ['Messages', String(a.messages)]
    ]],
    ['Numbers', [
      ['XP', String(a.xp)],
      ['Parties hosted', String(a.partiesHosted)],
      ['Parties joined', String(a.partiesJoined)],
      ['Friends', String(a.friends)],
      ['Reports against', String(a.reports)]
    ]]
  ];

  return (
    <section className="rounded-[var(--radius-xl)] border border-[color-mix(in_srgb,var(--bad)_26%,var(--line))] bg-surface-1 p-4 sm:p-5">
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="flex w-full items-center gap-3 rounded-[var(--radius-l)] text-left outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-[color-mix(in_srgb,var(--bad)_15%,transparent)] text-bad">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2 20 4.5v6c0 4.3-3.2 7.6-8 9-4.8-1.4-8-4.7-8-9v-6L12 2Z" />
          </svg>
        </span>
        <span className="flex-1">
          <span className="block text-[15px] font-bold text-ink">Staff view</span>
          <span className="block text-[13px] text-mute">Account detail only moderators can see</span>
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
          className={cn('text-ghost transition-transform', open && 'rotate-180')}>
          <path d="m6 9.5 6 6 6-6" />
        </svg>
      </button>

      {open ? (
        <div className="mt-4 flex flex-col gap-3">
          {sections.map(([name, rows]) => (
            <div key={name}>
              <div className="mb-1.5 text-[13px] font-semibold text-mute">{name}</div>
              <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
                {rows.map(([k, v]) => (
                  <div key={k} className="rounded-[var(--radius-s)] border border-line-soft bg-surface-2 px-3 py-2">
                    <div className="text-[12px] text-mute">{k}</div>
                    <div className="selectable text-[14px] font-semibold break-words text-ink">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

/**
 * The one friend action that makes sense right now.
 *
 * Which button you get depends on who asked whom: a request they sent you is
 * an Accept, a request you sent is a disabled Requested, and a block is the
 * only thing that can be undone from here.
 */
function FriendAction({ user, relation }: { user: PublicUser; relation: Relation }) {
  const queryClient = useQueryClient();
  const me = useSession((s) => s.me);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function run(path: string, body?: Record<string, unknown>, note?: string) {
    setBusy(true);
    try {
      const res = await api.post<{ status?: string }>(path, body);
      toast(note ?? (res.status === 'accepted' ? 'You are now friends' : 'Done'), 'good');
      await queryClient.invalidateQueries({ queryKey: ['user', user.username] });
      await queryClient.invalidateQueries({ queryKey: ['friends'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  if (relation?.status === 'accepted') {
    return <Button kind="plain" disabled icon={<Icons.CheckCircle size={15} />}>Friends</Button>;
  }

  if (relation?.status === 'blocked') {
    if (relation.actorId !== me?.id) return null;
    return (
      <Button kind="outline" disabled={busy} icon={<Icons.Ban size={15} />} onClick={() => void run('/api/friends/unblock', { userId: user.id }, 'Unblocked')}>
        Unblock
      </Button>
    );
  }

  if (relation?.status === 'pending') {
    const theyAsked = relation.actorId === user.id;
    if (!theyAsked) return <Button kind="plain" disabled icon={<Icons.CheckCircle size={15} />}>Requested</Button>;
    return (
      <>
        <Button kind="primary" disabled={busy} onClick={() => void run('/api/friends/' + relation.id + '/accept', undefined, 'You are now friends')}>
          Accept
        </Button>
        <Button kind="outline" disabled={busy} onClick={() => void run('/api/friends/' + relation.id + '/decline', undefined, 'Declined')}>
          Decline
        </Button>
      </>
    );
  }

  return (
    <Button kind="outline" disabled={busy} icon={<Icons.Plus size={15} />} onClick={() => void run('/api/friends/request', { username: user.username }, 'Friend request sent')}>
      Add friend
    </Button>
  );
}
